// MBA Lab — Org Alignment Workbench UI. Imports only lib/core.js and
// lib/alignment-model.js. Event delegation over data-element-key/
// data-pair-rating/data-pair-note means the 4 element fields and 6 pair
// sections never need individually-wired listeners, matching snapshot's and
// readiness's precedent.
import { store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  TOTAL_ELEMENTS,
  TOTAL_PAIRS,
  createInitialState,
  parseSavedState,
  serializeForSave,
  setSituation,
  setElementNote,
  setPairRating,
  setPairNote,
  countElementsFilled,
  countPairsRated,
  buildReadout,
} from './lib/alignment-model.js';
import { ELEMENTS, PAIRS } from './lib/alignment-elements.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'org-alignment-workbench';
const AUTOSAVE_DEBOUNCE_MS = 400;
const ANNOUNCE_DEBOUNCE_MS = 1200;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class OrgAlignmentWorkbench {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.alignmentStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.saveTimer = null;
    this.announceTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.dock = initToolDock({
      dock: byId('alignment-dock'),
      jumpTarget: byId('alignment-readout'),
      autoVisibility: false,
      hideJumpUntilComplete: true,
    });

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('alignment-form');
    this.els = {
      situation: byId('alignment-situation'),
      progressText: byId('alignment-progress-text'),
      startOver: byId('alignment-start-over'),
      readoutSection: byId('alignment-readout'),
      readoutIncomplete: byId('alignment-readout-incomplete'),
      mostMismatched: byId('alignment-most-mismatched'),
      goodFit: byId('alignment-group-good-fit'),
      someFriction: byId('alignment-group-some-friction'),
      realMismatch: byId('alignment-group-real-mismatch'),
      summaryAnnouncer: byId('alignment-summary-announcer'),
    };
  }

  loadInitialState() {
    const saved = this.alignmentStore.load();
    if (!saved) return createInitialState();
    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    return createInitialState();
  }

  applyStateToForm() {
    this.els.situation.value = this.state.situation;
    for (const e of ELEMENTS) {
      const el = this.form.querySelector(`[data-element-key="${e.key}"]`);
      if (el) el.value = this.state.elementNotes[e.key];
    }
    for (const p of PAIRS) {
      const rating = this.state.pairRatings[p.key];
      if (rating) {
        const radio = this.form.querySelector(`input[data-pair-rating="${p.key}"][value="${rating}"]`);
        if (radio) radio.checked = true;
      }
      const noteEl = this.form.querySelector(`[data-pair-note="${p.key}"]`);
      if (noteEl) noteEl.value = this.state.pairNotes[p.key];
    }
  }

  wireEvents() {
    this.form.addEventListener('input', (e) => this.handleInput(e));
    this.form.addEventListener('change', (e) => this.handleChange(e));
    this.els.startOver.addEventListener('click', () => this.handleStartOver());
  }

  handleInput(e) {
    const el = e.target;
    if (el === this.els.situation) {
      this.state = setSituation(this.state, el.value);
      this.afterChange();
      return;
    }
    const elementEl = el.closest('[data-element-key]');
    if (elementEl && this.form.contains(elementEl)) {
      this.state = setElementNote(this.state, elementEl.dataset.elementKey, elementEl.value);
      this.afterChange();
      return;
    }
    const noteEl = el.closest('[data-pair-note]');
    if (noteEl && this.form.contains(noteEl)) {
      this.state = setPairNote(this.state, noteEl.dataset.pairNote, noteEl.value);
      this.afterChange();
    }
  }

  handleChange(e) {
    const el = e.target.closest('input[data-pair-rating]');
    if (!el || !this.form.contains(el)) return;
    this.state = setPairRating(this.state, el.dataset.pairRating, el.value);
    this.afterChange();
  }

  afterChange() {
    this.markEngaged();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.scheduleAutosave();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
    this.dock?.show();
  }

  renderProgress() {
    const elementsFilled = countElementsFilled(this.state);
    const pairsRated = countPairsRated(this.state);
    const text = `${elementsFilled} of ${TOTAL_ELEMENTS} elements described, ${pairsRated} of ${TOTAL_PAIRS} pairs rated`;
    this.els.progressText.textContent = text;
    this.dock?.setReading(`${pairsRated} of ${TOTAL_PAIRS} pairs rated`);
  }

  renderReadoutIfComplete() {
    const readout = buildReadout(this.state);
    this.dock?.setComplete(!!readout);
    if (!readout) {
      this.els.readoutSection.hidden = true;
      this.els.readoutIncomplete.hidden = false;
      if (this.announceTimer) clearTimeout(this.announceTimer);
      this.els.summaryAnnouncer.textContent = '';
      return;
    }

    this.els.readoutIncomplete.hidden = true;
    this.els.readoutSection.hidden = false;

    this.renderMostMismatched(readout.mostMismatched, readout.mostMismatchedText, readout.mostMismatchedLead);
    this.renderGroup(this.els.realMismatch, readout.realMismatch);
    this.renderGroup(this.els.someFriction, readout.someFriction);
    this.renderGroup(this.els.goodFit, readout.goodFit);

    this.scheduleAnnouncement(
      `Your alignment readout: ${readout.realMismatch.length} real mismatches, ` +
        `${readout.someFriction.length} with some friction, ${readout.goodFit.length} a good fit.`
    );

    if (!this.hasFiredResult) {
      this.hasFiredResult = true;
      // Coarse counts only - never the free-text situation/notes, which can
      // describe a real, specific internal organizational problem.
      trackToolAction(TOOL_NAME, 'result', {
        real_mismatch_count: readout.realMismatch.length,
        some_friction_count: readout.someFriction.length,
        good_fit_count: readout.goodFit.length,
      });
    }
  }

  /** The sentence itself is fully pre-formatted by alignment-model.js's
   * buildReadout() - correct English list-joining and either/any-of-them
   * wording for any tie count lives there, once, where it's actually
   * unit-testable, rather than reassembled here from parts. mostMismatchedLead
   * is always an exact leading substring of mostMismatchedText (the element
   * name(s), '' when there's nothing to emphasize) - splitting it into its
   * own <strong> here preserves this tool's original emphasis styling
   * (`.tool-alignment-insight strong`) without this file doing any grammar
   * work of its own. */
  renderMostMismatched(labels, mostMismatchedText, mostMismatchedLead) {
    clearChildren(this.els.mostMismatched);
    const p = document.createElement('p');
    p.className = labels.length ? 'tool-alignment-insight' : 'tool-snapshot-incomplete-note';
    if (mostMismatchedLead) {
      const strong = document.createElement('strong');
      strong.textContent = mostMismatchedLead;
      p.appendChild(strong);
      p.appendChild(document.createTextNode(mostMismatchedText.slice(mostMismatchedLead.length)));
    } else {
      p.textContent = mostMismatchedText;
    }
    this.els.mostMismatched.appendChild(p);
  }

  renderGroup(container, items) {
    clearChildren(container);
    for (const item of items) {
      const card = document.createElement('div');
      card.className = 'tool-alignment-pair-card';

      const title = document.createElement('h3');
      title.textContent = item.label;
      card.appendChild(title);

      const prompt = document.createElement('p');
      prompt.className = 'tool-alignment-pair-prompt';
      prompt.textContent = item.prompt;
      card.appendChild(prompt);

      const text = document.createElement('p');
      text.textContent = item.text;
      card.appendChild(text);

      if (item.note) {
        const note = document.createElement('p');
        note.className = 'tool-alignment-pair-note';
        note.textContent = item.note;
        card.appendChild(note);
      }

      container.appendChild(card);
    }
  }

  /**
   * Mirrors roi.js's scheduleAnnouncement: the visible groups update
   * immediately, but a screen reader announcing every intermediate change
   * while someone revises earlier answers would be noisy. This mirrors a
   * summary into a separate visually-hidden aria-live region, debounced.
   */
  scheduleAnnouncement(text) {
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.els.summaryAnnouncer.textContent !== text) {
        this.els.summaryAnnouncer.textContent = text;
      }
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return;
    this.alignmentStore.save(toSave);
  }

  handleStartOver() {
    this.alignmentStore.deleteAccountState();
    this.state = createInitialState();
    this.hasFiredResult = false;
    this.form.reset();
    this.renderProgress();
    this.renderReadoutIfComplete();
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('alignment-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new OrgAlignmentWorkbench();
});
